import { ApiError } from "../../api/client";
import type { Session } from "../../api/types";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/toast-context";
import { usePurgeSession } from "./hooks";

interface RemoveSessionDialogProps {
  /** The session to remove, or null when the dialog is closed. */
  session: Session | null;
  onClose: () => void;
}

/**
 * Operator removal (Task 5): purge a session from the console. Unlike "End"
 * (which only disconnects a live agent), this soft-deletes the persisted row and
 * drops any in-memory session, clearing a ghost/stale session — including one
 * for an agent that is already offline.
 */
export function RemoveSessionDialog({
  session,
  onClose,
}: RemoveSessionDialogProps) {
  const toast = useToast();
  const purge = usePurgeSession();

  function onConfirm() {
    if (!session) return;
    purge.mutate(session.id, {
      onSuccess: () => {
        toast.success(
          "Session removed",
          `Cleared the session for ${session.agent_name}.`,
        );
        onClose();
      },
      onError: (err) => {
        toast.error(
          "Could not remove session",
          err instanceof ApiError
            ? `${err.message}${err.code ? ` (${err.code})` : ""}`
            : "The relay did not respond. The session was not removed.",
        );
      },
    });
  }

  return (
    <ConfirmDialog
      open={session != null}
      title="Remove session?"
      danger
      busy={purge.isPending}
      confirmLabel="Remove session"
      cancelLabel="Keep session"
      onConfirm={onConfirm}
      onCancel={onClose}
      body={
        session ? (
          <p style={{ marginTop: 0 }}>
            Remove the session for <strong>{session.agent_name}</strong> from the
            console. Any live connection is dropped and the row disappears from
            the list. This clears a stale or ghost session and cannot be undone.
          </p>
        ) : null
      }
    />
  );
}
